import { Box, FormControlLabel, Switch, Tab, Tabs, Typography } from '@mui/material';
import React from 'react';
import { connect } from 'react-redux';

import { TabPanel } from 'app/components/tab-panel';
import { Translate } from 'app/components/translate';
import { CrisRequestType } from 'app/constants/cris-request-type';
import { Store } from 'app/model';
import { makeSeamPattern, SeamPattern } from 'app/model/seam-pattern';
import { SeamRequest } from 'app/model/seam-request';
import { searchService } from 'app/services/search-service';
import { RootState } from 'app/store';
import { ModelWithoutId } from 'app/type/model-with-id';
import { Strings } from 'app/utils/strings';

import { DeployedPatterns } from './deployed-patterns';
import { PatternAccordion } from './pattern-accordion';
import { PatternEditor } from './pattern-editor';
import { PatternForm } from './pattern-form';
import { selectSelectedPattern } from '../slice';

interface PatternSplitEditorProps {
    request: SeamRequest;
    patterns: SeamPattern[];

    // redux props
    selectedPatternId?: string;

    store: Store | undefined;
}

interface PatternSplitEditorState {
    helpers: boolean;
    tabValue: number;
    remainingLookupData: ModelWithoutId<SeamPattern>;
}

class PatternSplitEditor extends React.Component<PatternSplitEditorProps, PatternSplitEditorState> {
    public constructor(props: PatternSplitEditorProps | Readonly<PatternSplitEditorProps>) {
        super(props);

        this.state = {
            helpers: false,
            tabValue: 0,
            remainingLookupData: makeSeamPattern(),
        };
    }

    public render(): React.ReactNode {
        const requestTypeStr = Strings.seamRequestType(this.props.request);
        const store = this.props.request.store;

        return (
            <Box display='flex'>
                <Box flexGrow={1} flexBasis='50%'>
                    <Box display='flex' alignItems='baseline'>
                        <Typography variant='h5'>
                            <Translate tkey={`headings:${requestTypeStr}`} /> Pattern: {store.name}
                        </Typography>
                        <Typography variant='h6'>
                            &nbsp;({this.props.selectedPatternId ? 'Updating' : 'Creating New'})&nbsp;
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={this.state.helpers}
                                    onChange={(event) => this.setState({ helpers: event.target.checked })}
                                />
                            }
                            label='Split Mode'
                        />
                    </Box>
                    <PatternEditor
                        request={this.props.request}
                        patterns={this.props.patterns}
                        store={this.props.store}
                    />
                </Box>
                {this.state.helpers ? (
                    <Box flexGrow={1} flexBasis='50%' pl={1}>
                        <Typography variant='h6'>Extra Tools</Typography>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                                value={this.state.tabValue}
                                onChange={(evt, value) => this.setState({ tabValue: Number(value) })}
                            >
                                <Tab label='Remaining Lookup' />
                                <Tab label='Other Patterns' />
                                <Tab label='Deployed Patterns' />
                            </Tabs>
                        </Box>
                        <TabPanel value={this.state.tabValue} index={0}>
                            <Typography variant='body2'>
                                You must save the pattern on the left to see it's affect on this side.
                            </Typography>
                            <PatternForm
                                data={this.state.remainingLookupData}
                                hideActions
                                correction={this.props.request.correction}
                                seamRequest={this.props.request}
                                targetStoreId={this.props.request.store.id}
                                onChange={(remainingLookupData) => this.setState({ remainingLookupData })}
                                onSearch={(opts, searchAfter) =>
                                    searchService.findRemaining(
                                        this.props.request,
                                        {
                                            type: CrisRequestType.STORE, // pattern.type,
                                            correction: this.props.request.correction ? '1' : '0',
                                            storeId: this.props.request.store.id,
                                            searchParams: this.state.remainingLookupData.data,
                                            searchAfter,
                                        },
                                        opts,
                                    )
                                }
                                store={this.props.store}
                            />
                        </TabPanel>
                        <TabPanel value={this.state.tabValue} index={1}>
                            {this.props.patterns.map((ptt, key) => {
                                const style =
                                    ptt.id === this.props.selectedPatternId
                                        ? { backgroundColor: 'rgb(150, 255, 150)' }
                                        : undefined;

                                return (
                                    <PatternAccordion
                                        key={key}
                                        sequence={key + 1}
                                        pattern={ptt}
                                        editable={false}
                                        style={style}
                                    />
                                );
                            })}
                        </TabPanel>
                        <TabPanel value={this.state.tabValue} index={2}>
                            <DeployedPatterns request={this.props.request} disableEdit />
                        </TabPanel>
                    </Box>
                ) : undefined}
            </Box>
        );
    }
}

const mapStateToProps = (state: RootState) => ({
    selectedPatternId: selectSelectedPattern(state)?.id,
});

const thunks = {};
const connected = connect(mapStateToProps, thunks)(PatternSplitEditor);

export { connected as PatternSplitEditor };
